import { AlertCircle, AlertTriangle, CheckCircle2, Info, Loader2 } from 'lucide-react';
import { cn } from './cn.js';

export type AlertTone = 'info' | 'warn' | 'alarm' | 'good';

const ALERT: Record<AlertTone, { frame: string; label: string; icon: typeof Info }> = {
  info: { frame: 'border-sky-600/20 bg-sky-50 text-sky-900', label: 'text-sky-700', icon: Info },
  warn: { frame: 'border-clay-600/20 bg-clay-50 text-amber-950', label: 'text-clay-700', icon: AlertTriangle },
  alarm: { frame: 'border-rust-600/20 bg-rust-50 text-rose-950', label: 'text-rust-700', icon: AlertCircle },
  good: { frame: 'border-herb-600/20 bg-herb-50 text-emerald-950', label: 'text-herb-700', icon: CheckCircle2 },
};

export interface AlertProps {
  readonly tone?: AlertTone;
  readonly title?: string;
  readonly children: React.ReactNode;
  readonly actions?: React.ReactNode;
  readonly className?: string;
}

/** Persistent banner with Lucide iconography. */
export function Alert({
  tone = 'info',
  title,
  children,
  actions,
  className,
}: AlertProps): React.ReactElement {
  const style = ALERT[tone];
  const Icon = style.icon;
  return (
    <div
      role={tone === 'alarm' ? 'alert' : 'status'}
      className={cn('flex items-start gap-3 rounded-card border px-4 py-3 shadow-xs', style.frame, className)}
    >
      <Icon className={cn('h-5 w-5 shrink-0 mt-0.5', style.label)} aria-hidden="true" />
      <div className="min-w-0 flex-1 text-sm leading-normal">
        {title !== undefined && <p className={cn('text-[13px] font-bold mb-0.5', style.label)}>{title}</p>}
        <div className="leading-relaxed">{children}</div>
      </div>
      {actions !== undefined && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}

export interface EmptyStateProps {
  readonly title: string;
  readonly hint?: string;
  readonly action?: React.ReactNode;
  readonly icon?: React.ReactNode;
  readonly className?: string;
}

/** Clean empty state with guidance and actionable CTA. */
export function EmptyState({
  title,
  hint,
  action,
  icon,
  className,
}: EmptyStateProps): React.ReactElement {
  return (
    <div
      className={cn(
        'flex flex-col items-center gap-3 rounded-card border border-dashed border-border-strong bg-card/60 px-6 py-12 text-center shadow-xs',
        className,
      )}
    >
      {icon !== undefined ? (
        <div className="grid h-12 w-12 place-items-center rounded-full bg-cream text-ink-soft">
          {icon}
        </div>
      ) : (
        <span aria-hidden="true" className="h-1.5 w-12 rounded-full bg-border-strong opacity-80" />
      )}
      <p className="text-base font-semibold text-ink">{title}</p>
      {hint !== undefined && <p className="max-w-md text-xs sm:text-sm text-ink-soft leading-relaxed">{hint}</p>}
      {action !== undefined && <div className="mt-2">{action}</div>}
    </div>
  );
}

/** Modern spinner loading state. */
export function Loading({
  label = 'Chargement…',
  className,
}: {
  readonly label?: string;
  readonly className?: string;
}): React.ReactElement {
  return (
    <div className={cn('flex items-center gap-2.5 text-sm text-ink-soft', className)} role="status">
      <Loader2 className="h-4 w-4 animate-spin text-herb-600" aria-hidden="true" />
      <span>{label}</span>
    </div>
  );
}

export interface ProgressBarProps {
  readonly value: number | null;
  readonly label?: string;
  readonly className?: string;
}

/** Upload / job progress. A `null` value means indeterminate. */
export function ProgressBar({ value, label, className }: ProgressBarProps): React.ReactElement {
  const known = value !== null;
  return (
    <div className={cn('w-full', className)}>
      {label !== undefined && (
        <div className="mb-1.5 flex items-center justify-between text-xs font-medium text-ink-soft">
          <span>{label}</span>
          {known && <span className="tabular font-mono text-[11px] font-semibold">{Math.round(value)} %</span>}
        </div>
      )}
      <div className="h-2 w-full overflow-hidden rounded-full bg-cream border border-border">
        {known ? (
          <div
            className="h-full rounded-full bg-herb-600 transition-[width] duration-200"
            style={{ width: `${Math.max(0, Math.min(100, value))}%` }}
          />
        ) : (
          <div className="h-full w-1/3 animate-pulse-soft rounded-full bg-clay-600" />
        )}
      </div>
    </div>
  );
}
